import {
  FocusLock,
  Popover,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import React from "react";
import { css } from "styled-components";
import { TheFieldset } from "components/TheComponents";
import AdminActionCheckbox from "./AdminActionCheckbox";
import { useDebounce } from "use-debounce";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import VerifierProvider, { useVerifierContext } from "../../../contexts/VerifierProvider";
import { Box, ButtonsBox, RootTable, TableBox, Form } from "./styles";
import SuspiciousLoginsIpDetails from "./SuspiciousLoginsIpDetails";
import { Pages, Button, ButtonBlue } from "components/common";
import {
  SuspiciousLoginItem,
  useSuspiciousLoginsQuery,
} from "hooks/admin/useSuspiciousLoginsQuery";
import { useAppContext } from "contexts";
import { triggerNotification } from "components/TheComponents/Notification";
import { ExceptionHandlingType } from "types/apiServiceTypes";
import Image from "next/image";
import WrewardsButton from "components/wrewards-ui-kit/Button";

const initialPagination = {
  skip: 0,
  take: 10,
};

type SelectedIp = {
  ip: string;
};

const AdminSuspiciousLogins = () => {
  const { accessFetch, apiService } = useAppContext();
  const { verify, closeMfe } = useVerifierContext();
  const queryClient = useQueryClient();
  const [skip, setSkip] = React.useState<number>(initialPagination.skip);
  const take: number = initialPagination.take;
  const [search, setSearch] = React.useState<string>("");
  const [selectedIps, setSelectedIps] = React.useState<SelectedIp[]>([]);
  const [debouncedSearch] = useDebounce(search, 600);

  const [withBlocked, setWithBlocked] = React.useState<boolean>(false);
  const [ignoreWhitelist, setIgnoreWhitelist] = React.useState<boolean>(false);
  const [withoutBannedUsers, setWithoutBannedUsers] = React.useState<boolean>(true);

  const [blockPopoverIp, setBlockPopoverIp] = React.useState<string | null>(null);
  const blockReasonInputRef = React.useRef<HTMLTextAreaElement>(null!);
  const banAllReasonInputRef = React.useRef<HTMLTextAreaElement>(null!);

  const {
    data: { items, totalCount },
    refetch,
    isFetching,
  } = useSuspiciousLoginsQuery({
    skip,
    take,
    search: debouncedSearch,
    withBlocked,
    ignoreWhitelist,
    withoutBannedUsers,
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const response = await accessFetch(
        `/user-login/suspicious-logins/refresh`,
        {
          method: "POST",
        },
        ExceptionHandlingType.AUTOMATIC
      );

      const data = await response?.json();

      if (!response?.ok) {
        return Promise.reject(data);
      }

      return data;
    },
    onSuccess: () => {
      refetch();
      triggerNotification({
        text: "Suspicious logins data refreshed successfully",
        type: "success",
      });
    },
  });

  const blockIpMutation = useMutation({
    mutationFn: async ({ ip, reason, otp }: { ip: string; reason: string; otp: string }) => {
      const response = await accessFetch(
        "/ip-black-list/add",
        {
          method: "POST",
          headers: { "x-otp": otp },
          body: JSON.stringify({ ip, reason }),
        },
        ExceptionHandlingType.AUTOMATIC
      );

      if (!response?.ok) {
        return Promise.reject();
      }

      return ip;
    },
    onSuccess: (ip) => {
      refetch();
      setBlockPopoverIp(null);
      triggerNotification({
        text: `IP ${ip} successfully blocked`,
        type: "success",
      });
    },
  });

  const unblockIpMutation = useMutation({
    mutationFn: async ({ ip, otp }: { ip: string; otp: string }) => {
      const response = await accessFetch(
        `/ip-black-list/${ip}`,
        {
          method: "DELETE",
          headers: { "x-otp": otp },
        },
        ExceptionHandlingType.AUTOMATIC
      );

      if (!response?.ok) {
        return Promise.reject();
      }

      return ip;
    },
    onSuccess: (ip) => {
      refetch();
      triggerNotification({
        text: `IP ${ip} successfully unblocked`,
        type: "success",
      });
    },
  });

  const onBlockIpSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>, ip: string, otp: string) => {
      e.preventDefault();
      const banReason = new FormData(e.target as HTMLFormElement).get("banReason") as string;

      if (banReason === "" || !banReason) {
        triggerNotification({ text: "Ban Reason Required", type: "error" });
        return blockReasonInputRef?.current?.focus?.();
      }

      blockIpMutation.mutateAsync(
        { ip, reason: banReason, otp },
        {
          onSettled: () => {
            closeMfe();
          },
        }
      );
    },
    [blockIpMutation, closeMfe]
  );

  const whitelistIpMutation = useMutation({
    mutationFn: async ({ ip, otp }: { ip: string; otp: string }) => {
      const response = await accessFetch(
        "/ip-white-list/add",
        {
          method: "POST",
          headers: { "x-otp": otp },
          body: JSON.stringify({ ip, reason: "Added from suspicious logins" }),
        },
        ExceptionHandlingType.AUTOMATIC
      );

      if (!response?.ok) {
        return Promise.reject();
      }

      return ip;
    },
    onSuccess: (ip) => {
      refetch();
      triggerNotification({
        text: `IP ${ip} successfully added to whitelist`,
        type: "success",
      });
    },
  });

  const unwhitelistIpMutation = useMutation({
    mutationFn: async ({ ip, otp }: { ip: string; otp: string }) => {
      const response = await accessFetch(
        `/ip-white-list/${ip}`,
        {
          method: "DELETE",
          headers: { "x-otp": otp },
        },
        ExceptionHandlingType.AUTOMATIC
      );

      if (!response?.ok) {
        return Promise.reject();
      }

      return ip;
    },
    onSuccess: (ip) => {
      refetch();
      triggerNotification({
        text: `IP ${ip} successfully removed from whitelist`,
        type: "success",
      });
    },
  });

  const onWhitelistClick = (ip: string, isWhitelisted: boolean) => {
    if (isWhitelisted) {
      verify(({ otp }) => {
        unwhitelistIpMutation.mutate(
          { ip, otp },
          {
            onSettled: () => {
              closeMfe();
            },
          }
        );
      });
    } else {
      verify(({ otp }) => {
        whitelistIpMutation.mutate(
          { ip, otp },
          {
            onSettled: () => {
              closeMfe();
            },
          }
        );
      });
    }
  };

  const onBlockIpClick = (ip: string, isBlocked: boolean) => {
    if (isBlocked) {
      verify(({ otp }) => {
        unblockIpMutation.mutate(
          { ip, otp },
          {
            onSettled: () => {
              closeMfe();
            },
          }
        );
      });
    } else {
      setBlockPopoverIp(ip);
    }
  };

  const cleanCacheForIp = async (ip: string) => {
    try {
      await apiService.request(`/user-login/suspicious-logins/${ip}/clean`, {
        method: "DELETE",
      });
    } catch (error) {
      console.error("Failed to clean cache for IP:", error);
    }
  };

  const banAllMutation = useMutation({
    mutationFn: async ({ ip, banReason, otp }: { ip: string; banReason: string; otp: string }) => {
      const response = await accessFetch(
        "/user-login/suspicious-logins/ban-all",
        {
          method: "DELETE",
          headers: { "x-otp": otp },
          body: JSON.stringify({ ip, banReason }),
        },
        ExceptionHandlingType.AUTOMATIC
      );

      const data = await response?.json();

      if (!response?.ok) {
        return Promise.reject();
      }

      return { ip, data };
    },
    onSuccess: async ({ ip, data }) => {
      await cleanCacheForIp(ip);

      queryClient.invalidateQueries({ queryKey: ["suspicious-logins-detail", ip] });

      refetch();

      const count = Array.isArray(data) ? data.length : (data?.count ?? data?.bannedCount ?? "all");

      triggerNotification({
        text: `Successfully banned ${count} users from IP ${ip}`,
        type: "success",
      });
    },
  });

  const onBanAllSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>, ip: string, otp: string) => {
      e.preventDefault();
      const banReason = new FormData(e.target as HTMLFormElement).get("banAllReason") as string;

      if (banReason === "" || !banReason) {
        triggerNotification({ text: "Ban Reason Required", type: "error" });
        return banAllReasonInputRef?.current?.focus?.();
      }

      banAllMutation.mutateAsync(
        { ip, banReason, otp },
        {
          onSettled: () => {},
        }
      );
    },
    [banAllMutation]
  );

  const onSearch = (searchValue: string) => {
    setSearch(searchValue);
    setSkip(0);
  };

  const onPage = (index: number) => {
    setSkip(index * take);
  };

  const toggleCollapse = (ip: string) => {
    setSelectedIps((prev) => {
      const itemToRemoveIndex = prev.findIndex((item) => item.ip === ip);
      if (itemToRemoveIndex !== -1) {
        return prev.filter((_, index) => index !== itemToRemoveIndex);
      } else {
        return [...prev, { ip }];
      }
    });
  };

  const isSelectedIp = (ip: string) => {
    return selectedIps.find((item) => item.ip === ip) !== undefined;
  };

  return (
    <>
      <Box>
        <div
          className="controls"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
              flexWrap: "nowrap",
            }}
          >
            <TheFieldset
              styles={css`
                flex: 1 1 auto;
                min-width: 200px;
                width: 100%;
                max-width: 100%;
                border: 1px #1e213a solid;
                height: 50px;
                line-height: 35px;
                border-radius: 8px;
                background-image: url("/images/PachinkoComponent/search-prefix.png");
                background-repeat: no-repeat;
                background-size: 15px 15px;
                background-position: 1rem center;
                text-indent: 2rem;

                input {
                  width: 100%;
                }
              `}
            >
              <input
                type="text"
                placeholder="Search by IP, discord id, username"
                onChange={(e) => onSearch(e.target.value)}
                value={search}
              />
            </TheFieldset>

            <WrewardsButton
              variant="game-action-gray"
              style={{ height: 50, flexShrink: 0 }}
              onClick={(e) => {
                e.preventDefault();
                setWithBlocked(!withBlocked);
                setSkip(0);
              }}
            >
              <Switch isChecked={withBlocked === true} />
              With Blocked IPs
            </WrewardsButton>

            <WrewardsButton
              variant="game-action-gray"
              style={{ height: 50, flexShrink: 0 }}
              onClick={(e) => {
                e.preventDefault();
                setIgnoreWhitelist(!ignoreWhitelist);
                setSkip(0);
              }}
            >
              <Switch isChecked={ignoreWhitelist === true} />
              With Whitelisted IPs
            </WrewardsButton>

            <WrewardsButton
              variant="game-action-gray"
              style={{ height: 50, flexShrink: 0 }}
              onClick={(e) => {
                e.preventDefault();
                setWithoutBannedUsers(!withoutBannedUsers);
                setSkip(0);
              }}
            >
              <Switch isChecked={withoutBannedUsers === true} />
              Without banned users
            </WrewardsButton>
          </div>

          <Button
            height={"50px"}
            isDark
            onClick={() => refreshMutation.mutateAsync()}
            disabled={refreshMutation.isPending}
          >
            <ButtonBlue isDark style={{ whiteSpace: "nowrap", textTransform: "uppercase" }}>
              Hard Refresh
            </ButtonBlue>
          </Button>
        </div>

        <TableBox>
          <RootTable>
            <thead>
              <tr className="root-tr">
                <td colSpan={8}>
                  <div className="root-tr">
                    <div className="root-td">IP Address</div>
                    <div className="root-td">Unique Users</div>
                    <div className="root-td">Total Logins</div>
                    <div className="root-td">Same User Agents</div>
                    <div className="root-td">Suspicion Score</div>
                    <div className="root-td">Whitelist</div>
                    <div className="root-td">Block IP</div>
                    <div className="root-td">Details</div>
                  </div>
                </td>
              </tr>
            </thead>
            <tbody className="root-tbody">
              {items.map((item: SuspiciousLoginItem) => {
                return (
                  <tr className="root-tr" key={item.ip}>
                    <td colSpan={8}>
                      <div className="root-tr">
                        <div className="root-td">{item.ip}</div>
                        <div className="root-td">{item.uniqueUsersCount}</div>
                        <div className="root-td">{item.totalLoginsCount}</div>
                        <div className="root-td">{item.sameUserAgentsCount}</div>
                        <div className="root-td">{item.suspicionScore}</div>

                        <div className="root-td">
                          <div
                            style={{
                              opacity:
                                whitelistIpMutation.isPending || unwhitelistIpMutation.isPending
                                  ? 0.5
                                  : 1,
                              pointerEvents:
                                whitelistIpMutation.isPending || unwhitelistIpMutation.isPending
                                  ? "none"
                                  : "auto",
                              cursor: "pointer",
                            }}
                            onClick={() => onWhitelistClick(item.ip, item.isWhiteListed)}
                          >
                            <AdminActionCheckbox
                              checked={item.isWhiteListed}
                              onChange={() => {}}
                              size={20}
                            />
                          </div>
                        </div>

                        <div className="root-td">
                          <Popover
                            isOpen={blockPopoverIp === item.ip}
                            initialFocusRef={blockReasonInputRef}
                            onClose={() => setBlockPopoverIp(null)}
                            placement="left"
                            closeOnBlur={true}
                          >
                            <PopoverTrigger>
                              <div
                                style={{
                                  opacity:
                                    blockIpMutation.isPending || unblockIpMutation.isPending
                                      ? 0.5
                                      : 1,
                                  pointerEvents:
                                    blockIpMutation.isPending || unblockIpMutation.isPending
                                      ? "none"
                                      : "auto",
                                  cursor: "pointer",
                                }}
                                onClick={() => onBlockIpClick(item.ip, item.isBlackListed)}
                              >
                                <AdminActionCheckbox
                                  checked={item.isBlackListed}
                                  onChange={() => {}}
                                  size={20}
                                />
                              </div>
                            </PopoverTrigger>
                            {blockPopoverIp === item.ip ? (
                              <PopoverContent
                                p={5}
                                pt={8}
                                background={"#151c2c"}
                                borderColor={"#1e2944"}
                              >
                                <PopoverCloseButton />
                                <FocusLock>
                                  <Form
                                    onSubmit={(e) => {
                                      e.preventDefault();
                                      verify(({ otp }) => onBlockIpSubmit(e, item.ip, otp));
                                    }}
                                  >
                                    <div className="field-wrapper">
                                      <TheFieldset title="Reason">
                                        <textarea
                                          ref={blockReasonInputRef}
                                          placeholder={"Reason (Required)"}
                                          name="banReason"
                                        />
                                      </TheFieldset>
                                    </div>
                                    <div>
                                      <button type="submit" className="red">
                                        Block IP
                                      </button>
                                    </div>
                                  </Form>
                                </FocusLock>
                              </PopoverContent>
                            ) : null}
                          </Popover>
                        </div>

                        <div className="root-td">
                          <ButtonsBox>
                            <button
                              aria-expanded={isSelectedIp(item.ip)}
                              className="collapse"
                              onClick={() => toggleCollapse(item.ip)}
                            >
                              <Image
                                style={{
                                  filter: "brightness(0) invert(1)",
                                }}
                                alt="expand"
                                src="/images/icons/arrow-down.svg"
                                width={10}
                                height={10}
                              />
                            </button>
                          </ButtonsBox>
                        </div>
                      </div>
                      {isSelectedIp(item.ip) ? (
                        <SuspiciousLoginsIpDetails
                          ip={item.ip}
                          onClose={() => toggleCollapse(item.ip)}
                          banAllMutation={banAllMutation}
                          banAllReasonInputRef={banAllReasonInputRef}
                          onBanAllSubmit={onBanAllSubmit}
                        />
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </RootTable>
          {isFetching ? (
            <div className="center">
              <Spinner />
            </div>
          ) : null}
        </TableBox>

        <Pages
          skip={skip}
          take={take}
          total={totalCount}
          showInfo={false}
          onClick={(index) => {
            onPage(index);
          }}
        />
      </Box>
    </>
  );
};

const AdminSuspiciousLoginsWrapper = () => {
  return (
    <VerifierProvider>
      <AdminSuspiciousLogins />
    </VerifierProvider>
  );
};

export default AdminSuspiciousLoginsWrapper;
